import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ReactNode,
} from "react";
import "./Button.css";

/**
 * Button — Figma 버튼 프리미티브.
 *
 * Figma 변형과 1:1:
 *   variant  ← hierarchy (Primary | Secondary | Underline)
 *              primary/secondary = ButtonContained
 *              underline         = Text underline (문구 옆 inline)
 *   size     ← Large/Medium/Small/XSmall/XXSmall → lg/md/sm/xs/xxs
 *   상태(Enabled/Hover/Disabled) 는 CSS :hover / :disabled 로 표현.
 *
 * 폴리모픽: `href` 가 있으면 <a>, 없으면 <button> 으로 렌더(카드들이 둘 다 사용).
 * 모든 스타일 토큰은 src/tokens 에서 온다.
 */
export type ButtonVariant = "primary" | "secondary" | "underline";
export type ButtonSize = "lg" | "md" | "sm" | "xs" | "xxs";

interface ButtonOwnProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  icon?: boolean;
  children?: ReactNode;
  className?: string;
}

type ButtonAsButton = ButtonOwnProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonOwnProps> & {
    href?: undefined;
  };
type ButtonAsAnchor = ButtonOwnProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonOwnProps> & {
    href: string;
  };

export type ButtonProps = ButtonAsButton | ButtonAsAnchor;

export function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  icon = false,
  children,
  className,
  ...rest
}: ButtonProps) {
  const cls = [
    "ds-btn",
    `ds-btn--${variant}`,
    `ds-btn--${size}`,
    fullWidth && "ds-btn--full",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const labelCls = ["ds-btn__label", icon && "ds-btn__icon"].filter(Boolean).join(" ");
  const content =
    children != null ? (
      <span className={labelCls}>{children}</span>
    ) : icon ? (
      <span className="ds-btn__icon" aria-hidden="true" />
    ) : null;

  if (rest.href != null) {
    const anchorProps = rest as AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a className={cls} {...anchorProps}>
        {content}
      </a>
    );
  }

  const buttonProps = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button className={cls} type={buttonProps.type ?? "button"} {...buttonProps}>
      {content}
    </button>
  );
}

export default Button;
